/**
 * Checkout order body uses canonical payment slugs (sheen-BE / orderPaymentMethod).
 * Storefront payment accounts (PaymentContext) use display types like jazzcash, bank-transfer.
 */

/** @param {unknown} frontendType */
export function inferCanonicalFromStorefront(frontendType) {
  const key = String(frontendType || '').toLowerCase();
  if (key === 'cash-on-delivery') return 'cod';
  if (['credit-card', 'debit-card', 'stripe'].includes(key)) return 'card';
  if (key === 'bank-transfer') return 'bank_transfer';
  if (
    ['jazzcash', 'easypaisa', 'paypal', 'bitcoin', 'ethereum', 'usdt', 'usdc'].includes(key)
  ) {
    return 'digital_wallet';
  }
  return null;
}

/** @param {string} canonical @param {unknown} storefrontType */
export function storefrontMatchesCanonical(canonical, storefrontType) {
  if (!canonical) return false;
  const c = String(canonical).trim().toLowerCase();
  return inferCanonicalFromStorefront(storefrontType) === c;
}

/**
 * True if this storefront payment account row should appear under the given canonical slug.
 * COD is matched only to `cash-on-delivery`, not via inferCanonicalFromStorefront.
 * @param {string} canonical
 * @param {unknown} storefrontType
 */
export function canonicalMatchesStorefrontPayment(canonical, storefrontType) {
  const c = String(canonical || '').trim().toLowerCase();
  if (!c) return false;
  if (c === 'cod') {
    return String(storefrontType || '').toLowerCase() === 'cash-on-delivery';
  }
  return storefrontMatchesCanonical(canonical, storefrontType);
}

/**
 * Normalize GET /orders/checkout/payment-methods (or 400 `availablePaymentMethods`) into UI rows.
 * @param {unknown} raw
 * @returns {{ value: string, label: string, description: string }[]}
 */
export function normalizeCheckoutPaymentMethodsFromApi(raw) {
  if (!Array.isArray(raw)) return [];
  return raw
    .map((m, i) => {
      if (typeof m === 'string') {
        const v = m.trim();
        if (!v) return null;
        return { value: v, label: v.replace(/_/g, ' '), description: '' };
      }
      if (m && typeof m === 'object') {
        const value = m.value ?? m.slug ?? m.id;
        if (value == null || String(value).trim() === '') return null;
        const label =
          m.label ?? m.name ?? m.title ?? String(value).replace(/_/g, ' ');
        const description =
          m.description ?? m.details ?? m.subtitle ?? m.hint ?? '';
        return {
          value: String(value).trim(),
          label: String(label || value).trim(),
          description: String(description || '').trim(),
        };
      }
      return null;
    })
    .filter(Boolean);
}
